import * as Sentry from '@sentry/react';
import { ReasonPhrases } from 'http-status-codes';
import isString from 'lodash/isString';
import { pushError } from '@asurion-core/frontend-utils/lib/WebObservability';
import environment from '../environment';

export class BadRequestError extends Error {
  constructor(data) {
    const message = isString(data) ? data : ReasonPhrases.BAD_REQUEST;
    super(message);

    this.data = data;
  }
}

export class InternalServerError extends Error {
  constructor(data) {
    const message = isString(data) ? data : ReasonPhrases.INTERNAL_SERVER_ERROR;
    super(message);

    this.data = data;
  }
}

/*
 * Finds a string error message inside a nested Parse like error object.
 * @param {Object} nestedMessage
 * @param {String} defaultMessage
 * @return {String} - mosted nested error string, or a default supplied error or 'Unknown Error'
 */
export const getNestedErrorMessage = (nestedMessage, defaultMessage) => {
  while (typeof nestedMessage === 'object') {
    nestedMessage = nestedMessage.message;
  }
  if (typeof nestedMessage === 'string' && nestedMessage) {
    return nestedMessage;
  }

  return defaultMessage || 'Unknown Error';
};

export const getZendeskErrorDescription = (error) => {
  return (
    (error.message &&
      error.message.details &&
      error.message.details.base &&
      error.message.details.base.length &&
      error.message.details.base[0].description) ||
    null
  );
};

/**
 * Parse and extract error message with throw express-validation middleware
 * @param {Object} error
 * @param {Object} error.response
 * @param {String} error.response.text
 * @return {Object} - like {password: "field is required"}
 */
export const getApiValidationErrorMessage = (error) => {
  const errorMessages = {};
  try {
    const [errorType, rest] = error.response.text.split(' - "');
    if (errorType === 'validation error') {
      const [field, errorMessage] = rest.split('" ');
      if (field && errorMessage) {
        errorMessages[field] = errorMessage;
      }
    }
  } catch (_) {}

  return errorMessages;
};

/** This function acts as a wrapper to Sentry's captureException
 *  This is needed because sometimes errors just show up as [object Object] in Sentry which is not helpful

 * @param  {Any} error
 */
export const captureException = (error) => {
  // if error is just a string, we should use the captureMessage function
  if (error instanceof String) {
    return Sentry.captureMessage(error);
  }

  if (environment.observability.isTelemetryEnabled) {
    try {
      pushError({ message: error });
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  let err2Use;
  // errors that are instances of Error seem to work fine in Sentry
  if (error instanceof Error) {
    err2Use = error;
  } else {
    try {
      err2Use = JSON.stringify(error);
    } catch (err) {
      err2Use = error;
    }
  }

  return Sentry.captureException(err2Use);
};

export class PenaltyBoxError extends Error {
  constructor(message, header, expirationTime) {
    super();
    this.name = 'PenaltyBoxError';
    this.expirationTime = expirationTime;
    this.header = header;
    this.message = message;
  }
}

export class UnhandledDecisionTreeCaseMultipleOrders extends Error {
  constructor(message) {
    super();
    this.name = 'UnhandledDecisionTreeCaseMultipleOrders';
    this.message = message + ` Please cancel one subscription at a time.`;
  }
}

export class UnhandledDecisionTreeCase extends Error {}

export class NegativeDiscount extends UnhandledDecisionTreeCase {
  constructor(previousDiscount) {
    super();
    this.message = `Discount was calculated as a negative: ${previousDiscount}`;
    this.name = 'NegativeDiscount';
  }
}

export class UnsupportedCarrier extends UnhandledDecisionTreeCase {
  constructor(carrier) {
    super();
    this.message = `Unsupported Shippo Carrier: ${carrier}`;
    this.name = 'UnsupportedCarrier';
  }
}

export class UnexpectedOrderingSystem extends UnhandledDecisionTreeCase {
  constructor(orderingSystem) {
    super();
    this.message = `Unexpected Ordering System: ${orderingSystem}`;
    this.name = 'UnexpectedOrderingSystem';
  }
}

export class MultipleTrackingNumberError extends UnhandledDecisionTreeCase {
  constructor() {
    super();
    this.message = `Multiple tracking numbers in single order shipment`;
    this.name = 'MultipleTrackingNumberError';
  }
}

export class UnexpectedShippingStatus extends UnhandledDecisionTreeCase {
  constructor(status) {
    super();
    this.message = `Unexpected Shipping status: ${status}`;
    this.name = 'UnexpectedShippingStatus';
  }
}

export class UnexpectedDecisionTreeEdge extends UnhandledDecisionTreeCase {
  constructor(edge) {
    super();
    this.message = `Unexpected decision tree edge edge: ${edge}`;
    this.name = 'UnexpectedDecisionTreeEdge';
  }
}

export class ConfigurableNodeError extends Error {
  constructor(message, cause) {
    super();
    this.name = 'ConfigurableNodeError';
    this.message = message;
    this.cause = cause;
  }
}

export class UsernameMismatchError extends Error {
  constructor(message, originalUsername, idpUsername) {
    super();
    this.name = 'UserMismatchError';
    this.message = message;
    this.originalUsername = message;
    this.idpUsername = message;
  }
}

/**
 * Extracts error message from API response with fallbacks
 * @param {Object} error - The error object from API response
 * @param {string} defaultMessage - Default message to use if no specific message found
 * @returns {string} - Extracted error message
 */
export const extractApiErrorMessage = (
  error,
  defaultMessage = 'An error occurred while processing your request. Please try again.'
) => {
  // Handle 403 Forbidden errors specifically
  if (error.response?.status === 403) {
    return (
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      'Access is denied. Please contact your administrator.'
    );
  }

  // Handle other error types with message extraction
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }

  if (error.message) {
    return error.message;
  }

  return defaultMessage;
};
